import React from 'react';
import { HeadingContainer, IconWrapper, H1 as StyledH1, H2 as StyledH2, H3 as StyledH3, H4 as StyledH4 } from './styles';
import { HeadingProps } from './types';

const headingComponents = {
  h1: StyledH1,
  h2: StyledH2,
  h3: StyledH3,
  h4: StyledH4,
};

export const Heading = React.forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ level = 'h2', children, icon, iconPosition = 'left', className, ...props }, ref) => {
    const HeadingComponent = headingComponents[level];

    if (!icon) {
      return (
        <HeadingComponent ref={ref as any} className={className} {...props}>
          {children}
        </HeadingComponent>
      );
    }

    return (
      <HeadingContainer className={className}>
        {iconPosition === 'left' && icon && <IconWrapper>{icon}</IconWrapper>}
        <HeadingComponent ref={ref as any} {...props}>
          {children}
        </HeadingComponent>
        {iconPosition === 'right' && icon && <IconWrapper>{icon}</IconWrapper>}
      </HeadingContainer>
    );
  }
);

Heading.displayName = 'Heading';

// Individual heading components with icon support
type IndividualHeadingProps = Omit<HeadingProps, 'level'>;

export const H1 = React.forwardRef<HTMLHeadingElement, IndividualHeadingProps>(
  ({ children, icon, iconPosition = 'left', className, ...props }, ref) => {
    if (!icon) {
      return (
        <StyledH1 ref={ref as any} className={className} {...props}>
          {children}
        </StyledH1>
      );
    }

    return (
      <HeadingContainer className={className}>
        {iconPosition === 'left' && <IconWrapper>{icon}</IconWrapper>}
        <StyledH1 ref={ref as any} {...props}>
          {children}
        </StyledH1>
        {iconPosition === 'right' && <IconWrapper>{icon}</IconWrapper>}
      </HeadingContainer>
    );
  }
);

H1.displayName = 'H1';

export const H2 = React.forwardRef<HTMLHeadingElement, IndividualHeadingProps>(
  ({ children, icon, iconPosition = 'left', className, ...props }, ref) => {
    if (!icon) {
      return (
        <StyledH2 ref={ref as any} className={className} {...props}>
          {children}
        </StyledH2>
      );
    }

    return (
      <HeadingContainer className={className}>
        {iconPosition === 'left' && <IconWrapper>{icon}</IconWrapper>}
        <StyledH2 ref={ref as any} {...props}>
          {children}
        </StyledH2>
        {iconPosition === 'right' && <IconWrapper>{icon}</IconWrapper>}
      </HeadingContainer>
    );
  }
);

H2.displayName = 'H2';

export const H3 = React.forwardRef<HTMLHeadingElement, IndividualHeadingProps>(
  ({ children, icon, iconPosition = 'left', className, ...props }, ref) => {
    if (!icon) {
      return (
        <StyledH3 ref={ref as any} className={className} {...props}>
          {children}
        </StyledH3>
      );
    }

    return (
      <HeadingContainer className={className}>
        {iconPosition === 'left' && <IconWrapper>{icon}</IconWrapper>}
        <StyledH3 ref={ref as any} {...props}>
          {children}
        </StyledH3>
        {iconPosition === 'right' && <IconWrapper>{icon}</IconWrapper>}
      </HeadingContainer>
    );
  }
);

H3.displayName = 'H3';

export const H4 = React.forwardRef<HTMLHeadingElement, IndividualHeadingProps>(
  ({ children, icon, iconPosition = 'left', className, ...props }, ref) => {
    if (!icon) {
      return (
        <StyledH4 ref={ref as any} className={className} {...props}>
          {children}
        </StyledH4>
      );
    }

    return (
      <HeadingContainer className={className}>
        {iconPosition === 'left' && <IconWrapper>{icon}</IconWrapper>}
        <StyledH4 ref={ref as any} {...props}>
          {children}
        </StyledH4>
        {iconPosition === 'right' && <IconWrapper>{icon}</IconWrapper>}
      </HeadingContainer>
    );
  }
);

H4.displayName = 'H4';

export type { HeadingProps, HeadingLevel } from './types';
